/**
 * Navigation information architecture — the single source of truth for the
 * sidebar, the command palette's "Go to" group, and the landing-page picker.
 *
 * Before this module the sidebar carried its own hardcoded copy of every
 * route (label, icon, path, group) while `registry.ts` carried a second copy
 * for permission lookups. The two drifted: the sidebar had entries the
 * registry didn't and vice versa, and four routes with permission slugs were
 * unreachable because nobody added them to the sidebar arrays.
 *
 * Here, an item is a *reference* to a registry slug. Paths and permission
 * checks resolve through `FEATURE_REGISTRY`, so adding a route in one place
 * is enough.
 */
import { useMemo } from 'react';
import {
  Activity,
  BarChart3,
  Bell,
  Bike,
  Boxes,
  Building2,
  CalendarDays,
  ClipboardList,
  Factory,
  FileText,
  Gauge,
  Headset,
  History,
  Inbox,
  Layers,
  MapPin,
  Megaphone,
  MessageSquare,
  MessagesSquare,
  Monitor,
  Network,
  Package,
  PackageCheck,
  PackageX,
  PieChart,
  Receipt,
  Search,
  Settings,
  ShieldAlert,
  ShieldCheck,
  ShoppingCart,
  Sparkles,
  Store,
  Tags,
  Thermometer,
  Ticket,
  Timer,
  TrendingUp,
  Truck,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { useAccess } from './AccessProvider';
import { ALWAYS_VISIBLE_SLUGS, FEATURE_REGISTRY } from './registry';

/** How long a `since` date keeps its NEW sticker. */
export const NAV_NEW_WINDOW_DAYS = 30;

export interface NavItem {
  /**
   * Registry slug. Drives both the permission check and (unless `path` is
   * given) the destination.
   */
  slug?: string;
  /**
   * Explicit destination for screens that have no BI resource of their own —
   * today just Access Control, which is gated on superuser instead.
   */
  path?: string;
  label: string;
  icon: LucideIcon;
  /** Match the route exactly. Needed for index-ish paths like `/`. */
  exact?: boolean;
  /**
   * ISO date the screen shipped. Drives a NEW sticker that *expires*, unlike
   * the old boolean which left 27 of 49 items permanently badged.
   */
  since?: string;
  /**
   * Open in a new tab. `/wall` mounts outside `AppShell`, so navigating to it
   * in-app strands the user on a page with no sidebar and no way back.
   */
  external?: boolean;
  /** Superuser-only screens that carry no assignable resource slug. */
  superuserOnly?: boolean;
}

export interface NavGroup {
  id: string;
  label: string;
  items: NavItem[];
}

/**
 * Ordered navigation tree.
 *
 * Grouping notes, since the previous taxonomy actively misled people:
 *   - "People" used to hold stores, network and darkstore siting — none of
 *     which are people. Stores/network now stand alone and customers moved
 *     next to the growth analytics that describe them.
 *   - "Supply" mixed what we hold (inventory) with who we buy from
 *     (vendors, procurement). Those are different jobs, so different groups.
 *   - "Workspace" was a junk drawer of nine unrelated screens. It's gone.
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    id: 'dashboards',
    label: 'Dashboards',
    items: [
      { slug: 'overview', label: 'Overview', icon: Gauge, exact: true },
      { slug: 'live-ops', label: 'Live Ops', icon: Activity },
      { slug: 'strategic', label: 'Strategic', icon: BarChart3 },
      { slug: 'payments', label: 'Payments', icon: Wallet, exact: true },
      { slug: 'payments.refunds', label: 'Refund Settlement', icon: Wallet },
      { slug: 'wall', label: 'Ops Wall', icon: Monitor, external: true },
    ],
  },
  {
    id: 'orders',
    label: 'Orders & Delivery',
    items: [
      { slug: 'orders', label: 'Orders', icon: PackageCheck, exact: true },
      { slug: 'orders.sla-heatmap', label: 'SLA Heatmap', icon: Thermometer },
      { slug: 'orders.durations', label: 'Order Durations', icon: Timer },
      { slug: 'riders', label: 'Riders', icon: Bike, exact: true },
      { slug: 'riders.distribution', label: 'Rider Distribution', icon: PieChart },
      { slug: 'roster', label: 'Roster', icon: CalendarDays },
    ],
  },
  {
    id: 'network',
    label: 'Stores & Network',
    items: [
      { slug: 'stores', label: 'Store Health', icon: Store },
      { slug: 'network', label: 'Network Map', icon: Network, exact: true },
      { slug: 'network.darkstore-siting', label: 'Darkstore Siting', icon: MapPin },
    ],
  },
  {
    id: 'inventory',
    label: 'Inventory',
    items: [
      { slug: 'supply', label: 'Supply Overview', icon: Boxes },
      { slug: 'inventory', label: 'Stock Ledger', icon: Layers, exact: true },
      { slug: 'inventory.replenishment', label: 'Replenishment', icon: Truck },
      { slug: 'inventory.forecast', label: 'Stock Forecast', icon: TrendingUp },
      { slug: 'inventory.audits', label: 'Stock Audits', icon: Layers },
      { slug: 'inventory.transfers', label: 'Transfers', icon: Truck },
      {
        slug: 'inventory.lost-items',
        label: 'Lost Items',
        icon: PackageX,
        since: '2026-07-29',
      },
      { slug: 'shrinkage', label: 'Shrinkage', icon: ShieldAlert },
    ],
  },
  {
    id: 'sourcing',
    label: 'Sourcing',
    items: [
      { slug: 'vendors', label: 'Vendors', icon: Factory, exact: true },
      { slug: 'vendors.profitability', label: 'Vendor Profitability', icon: TrendingUp },
      { slug: 'procurement', label: 'Procurement', icon: ShoppingCart },
    ],
  },
  {
    id: 'catalog',
    label: 'Catalog',
    items: [
      { slug: 'products', label: 'Products', icon: Package, exact: true },
      { slug: 'products.brands', label: 'Brands', icon: Tags },
      { slug: 'products.categories', label: 'Categories', icon: Layers },
      { slug: 'products.forecast', label: 'Demand Forecast', icon: TrendingUp },
      { slug: 'products.map', label: 'Sales Map', icon: MapPin },
      { slug: 'products.map3d', label: '3D Building Map', icon: Building2 },
    ],
  },
  {
    id: 'growth',
    label: 'Customers & Growth',
    items: [
      { slug: 'customers', label: 'Customers', icon: Users, exact: true },
      { slug: 'growth', label: 'Growth Overview', icon: TrendingUp, exact: true },
      { slug: 'growth.rfm', label: 'RFM Segments', icon: PieChart },
      { slug: 'growth.abandonment', label: 'Cart Abandonment', icon: ShoppingCart },
      // `/growth/coupons` and `/growth/referrals` are intentionally absent:
      // this page already renders both as tabs, so listing all three made
      // the same data look like three separate features. Routes still work
      // for existing deep links.
      { slug: 'growth.coupons-referrals', label: 'Coupons & Referrals', icon: Ticket },
      { slug: 'search', label: 'Search Insights', icon: Search },
      { slug: 'wishlist', label: 'Wishlist', icon: Sparkles },
      { slug: 'waitlist', label: 'Waitlist', icon: ClipboardList },
      { slug: 'feedback', label: 'Feedback', icon: MessageSquare },
    ],
  },
  {
    id: 'support',
    label: 'Support',
    items: [
      { slug: 'crm.inbox', label: 'Inbox', icon: Inbox },
      { slug: 'crm.conversations', label: 'Conversations', icon: MessagesSquare, exact: true },
      { slug: 'crm.tickets', label: 'Tickets', icon: Ticket, exact: true },
      { slug: 'crm.refunds', label: 'Refunds', icon: Receipt },
      // Named "claims" so it stops reading as a duplicate of inventory
      // Shrinkage — both used to be labelled just "Shrinkage".
      { slug: 'crm.shrinkage', label: 'Shrinkage Claims', icon: ShieldAlert },
      { slug: 'crm.analytics.sla', label: 'SLA Analytics', icon: BarChart3 },
      {
        slug: 'crm.analytics.conversations',
        label: 'Support Analytics',
        icon: Headset,
        since: '2026-07-20',
      },
    ],
  },
  {
    id: 'reporting',
    label: 'Reports & Alerts',
    items: [
      { slug: 'reports', label: 'Reports', icon: FileText },
      { slug: 'alerts', label: 'Alerts', icon: Bell },
      { slug: 'notifications', label: 'Notifications', icon: Megaphone },
    ],
  },
  {
    id: 'administration',
    label: 'Administration',
    items: [
      { slug: 'settings.user-management', label: 'Users', icon: Users, exact: true },
      {
        path: '/settings/access',
        label: 'Access Control',
        icon: ShieldCheck,
        superuserOnly: true,
      },
      { slug: 'audit', label: 'Audit Log', icon: History },
      { slug: 'settings', label: 'Settings', icon: Settings, exact: true },
    ],
  },
];

/** Destination for a nav item. */
export function navItemPath(item: NavItem): string | null {
  if (item.path) return item.path;
  if (!item.slug) return null;
  return FEATURE_REGISTRY[item.slug]?.path ?? null;
}

/** True while `since` is inside the NEW window. */
export function isFresh(since: string | undefined, now: Date = new Date()): boolean {
  if (!since) return false;
  const shipped = Date.parse(since);
  if (!Number.isFinite(shipped)) return false;
  const ageDays = (now.getTime() - shipped) / 86_400_000;
  return ageDays >= 0 && ageDays <= NAV_NEW_WINDOW_DAYS;
}

/**
 * Paths that make no sense as a landing page: parameterised detail routes
 * (no id to substitute) and screens that mount outside the app shell.
 */
function isSelectableLanding(path: string): boolean {
  if (path.includes(':')) return false;
  if (path === '/wall') return false;
  return path.startsWith('/');
}

export interface LandingOption {
  path: string;
  label: string;
  group: string;
  icon: LucideIcon;
}

/**
 * Every nav destination that could reasonably be someone's home screen, in
 * sidebar order. Callers filter by permission — see `useLandingOptions`.
 */
export function landingCandidates(): (LandingOption & { slug?: string; superuserOnly?: boolean })[] {
  const out: (LandingOption & { slug?: string; superuserOnly?: boolean })[] = [];
  for (const group of NAV_GROUPS) {
    for (const item of group.items) {
      const path = navItemPath(item);
      if (!path || !isSelectableLanding(path)) continue;
      if (item.external) continue;
      out.push({
        path,
        label: item.label,
        group: group.label,
        icon: item.icon,
        slug: item.slug,
        superuserOnly: item.superuserOnly,
      });
    }
  }
  return out;
}

export interface ResolvedNavItem extends NavItem {
  path: string;
  fresh: boolean;
  /** Unread counter pill (CRM inbox). Omitted or 0 renders nothing. */
  count?: number;
}

export interface ResolvedNavGroup {
  id: string;
  label: string;
  items: ResolvedNavItem[];
}

/**
 * Nav filtered to what the signed-in user may actually open.
 *
 * `isReady` is surfaced rather than swallowed so the sidebar can render a
 * skeleton. The previous implementation returned the *full* list while access
 * was in flight, so restricted users watched 49 items paint and then vanish.
 */
export function useVisibleNav(): { groups: ResolvedNavGroup[]; isReady: boolean } {
  const { isReady, can, isSuperuser } = useAccess();

  return useMemo(() => {
    if (!isReady) return { groups: [], isReady: false };

    const groups: ResolvedNavGroup[] = [];
    for (const group of NAV_GROUPS) {
      const items: ResolvedNavItem[] = [];
      for (const item of group.items) {
        const path = navItemPath(item);
        if (!path) continue;
        if (item.superuserOnly && !isSuperuser) continue;
        if (!item.superuserOnly && !isSuperuser) {
          if (!item.slug) continue;
          if (!ALWAYS_VISIBLE_SLUGS.has(item.slug) && !can(item.slug)) continue;
        }
        items.push({ ...item, path, fresh: isFresh(item.since) });
      }
      if (items.length) groups.push({ id: group.id, label: group.label, items });
    }

    // A user with no grants at all still needs somewhere to stand. Settings
    // is the only safe destination: the old fallback also offered Overview,
    // which bounced straight to /forbidden when `overview` was denied.
    if (groups.length === 0) {
      const settingsPath = FEATURE_REGISTRY.settings?.path;
      if (settingsPath) {
        groups.push({
          id: 'administration',
          label: 'Administration',
          items: [
            { slug: 'settings', label: 'Settings', icon: Settings, exact: true, path: settingsPath, fresh: false },
          ],
        });
      }
    }

    return { groups, isReady: true };
  }, [isReady, isSuperuser, can]);
}

/**
 * First destination the user can actually open, in sidebar order. Used as the
 * landing fallback and as the escape hatch on the Forbidden screen so nobody
 * is ever left with sign-out as their only option.
 */
export function useFirstAccessiblePath(): string | null {
  const { groups } = useVisibleNav();
  return useMemo(() => {
    for (const group of groups) {
      for (const item of group.items) {
        if (item.external) continue;
        if (!isSelectableLanding(item.path)) continue;
        // Settings is viewable by anyone holding any resource, so it is a
        // poor suggestion unless it is genuinely all they have.
        if (item.slug === 'settings') continue;
        return item.path;
      }
    }
    return FEATURE_REGISTRY.settings?.path ?? null;
  }, [groups]);
}

/**
 * Landing-page choices the user is actually allowed to open.
 *
 * The picker used to be seven hardcoded options with no permission check, so
 * a user could set their home page to something they'd be bounced off, and
 * then get redirected there on every cold boot.
 */
export function useLandingOptions(): LandingOption[] {
  const { can, isSuperuser } = useAccess();
  return useMemo(
    () =>
      landingCandidates()
        .filter((option) => {
          if (isSuperuser) return true;
          if (option.superuserOnly) return false;
          if (!option.slug) return false;
          return ALWAYS_VISIBLE_SLUGS.has(option.slug) || can(option.slug);
        })
        .map(({ path, label, group, icon }) => ({ path, label, group, icon })),
    [can, isSuperuser],
  );
}

/**
 * Whether `path` is a landing target this user may open. Used to validate the
 * stored preference before the router honours it.
 */
export function useCanUseLanding(): (path: string | undefined | null) => boolean {
  const options = useLandingOptions();
  return useMemo(() => {
    const allowed = new Set(options.map((o) => o.path));
    return (path: string | undefined | null) => (path ? allowed.has(path) : false);
  }, [options]);
}
